import type { PipelineDoc } from './types';

/**
 * Content-Pipeline (CUR-1571).
 *
 * Quellen der Beschreibung: docs/wiki/Data/pipeline-content-stufen.md,
 * pipeline-content-fachliche-sicherung.md, docs/content-pipeline/*.
 * Die Zahlen sind am 22.09.2026 direkt gegen den Seed gezaehlt, nicht aus
 * docs/content-pipeline/content-stats.json uebernommen — die Datei steht auf
 * Stand 2026-06-04 und behauptet 40 Themen / 263 Lektionen.
 */
export const contentPipeline: PipelineDoc = {
  slug: 'content',
  title: 'Content-Pipeline',
  claim: 'Fünf Stufen vom Themenplan zur geprüften Lektion — jede mit definiertem Eingang, definiertem Artefakt und eigenem Wächter.',
  asOf: '2026-09-22',

  summary: [
    'Lerninhalte entstehen nicht in einem Rutsch, sondern in fünf Stufen, die sich gegenseitig nichts glauben. Jede Stufe hat genau einen Eingang (einen Themen-Slug), liest festgelegte Quellen und schreibt genau ein Artefakt an einen festgelegten Ort. Was eine Stufe nicht geschrieben hat, existiert für die nächste nicht.',
    'Ausgeführt werden die Stufen von Skills — versionierte, im Repo liegende Arbeitsanweisungen für ein Sprachmodell, die sich wie Werkzeuge aufrufen lassen. Das Fachliche kommt aus einer zweiten Schicht: einer Dozenten-Persona je Domäne. Beide sind austauschbar, ohne die Strecke anzufassen.',
    'Die fünf Stufen gibt es in zwei Varianten: Migration eines bestehenden Quellartikels und Neuerstellung ohne Vorlage. Die Werkzeuge sind dieselben, nur Stufe 1 und 2 unterscheiden sich im Eingang.',
  ],

  facts: [
    { label: 'Themen', value: '47 / 15 / 15', hint: 'Heilpraktiker · Psychotherapie · Anthroposophie', source: 'gezählt: prisma/seed/content*/topics.json' },
    { label: 'Lektionen', value: '568', hint: '300 · 73 · 195 über die drei Inhaltsbäume', source: 'gezählt: prisma/seed/content*/*/lessons.json' },
    { label: 'Prüfungsfragen', value: '1.397', hint: 'themenbegleitend, zusätzlich zum Prüfungsarchiv', source: 'gezählt: questions.json' },
    { label: 'Karteikarten', value: '3.530', source: 'gezählt: flashcards.json' },
    { label: 'Glossareinträge', value: '1.936', source: 'gezählt: glossary.json' },
    { label: 'Fallbeispiele', value: '241', source: 'gezählt: cases.json' },
    { label: 'Selbsttest-Fragen', value: '2.527', source: 'gezählt: self-test-questions.json' },
  ],

  stages: [
    {
      id: 'themenplan',
      nr: '1',
      title: 'Themenplan',
      purpose: 'Das Thema in Lektionen schneiden, bevor eine Zeile Inhalt entsteht.',
      input: 'Themen-Slug, z. B. respirationstrakt',
      output: 'docs/content/plans/content-plan-{slug}.md',
      tool: 'Skill content-themenplan',
      detail: [
        'Gelesen werden topics.json, ein Referenz-Thema und die Fragen des Prüfungsarchivs zum Thema. Aus der Prüfungsrelevanz folgt die Gewichtung: was oft gefragt wird, bekommt eine eigene Lektion statt eines Absatzes.',
        'Das Ergebnis ist ein Plan mit Lektionsaufteilung, Lernzielen und geschätzter Dauer — bewusst als Markdown-Dokument und nicht als JSON, weil hier noch ein Mensch widersprechen soll.',
      ],
      risk: 'Ein zu grober Schnitt rächt sich erst in Stufe 2, wenn Lektionen unterschiedlich lang geraten.',
    },
    {
      id: 'lektionen',
      nr: '2',
      title: 'Lektionen',
      purpose: 'Den eigentlichen Lehrtext schreiben, in der Struktur, die App und Vertonung erwarten.',
      input: 'Themenplan aus Stufe 1',
      output: 'prisma/seed/content/{slug}/lessons.json',
      tool: 'Skill content-lessons + Dozenten-Persona',
      detail: [
        'Eine Lektion ist kein Fließtext, sondern eine Folge typisierter Abschnitte — Absatz, Tabelle, Merkkarte, Bild, Warnhinweis. Dieser Typ entscheidet später, wie der Abschnitt gesprochen wird: eine Tabelle wird vertont als erzählte Prosa, nicht als vorgelesenes Raster.',
        'Verbindlich ist docs/content-pipeline/lesson-contract.md — die Bauanleitung dafür, was eine gute Lektion ausmacht. Sie ist die Quelle, gegen die später geprüft wird.',
        'Die fachliche Schicht kommt aus der Persona: heilpraktiker-dozent, hpp-dozent oder anthro-dozent. Sie trägt Fachkorrektheit und die jeweilige Kompetenzgrenze — etwa dass eine Heilpraktiker-Erlaubnis keine Approbation ist.',
      ],
      risk: 'Wird der Text später geändert, ist die Vertonung veraltet. Genau dafür gibt es den Drift-Wächter.',
    },
    {
      id: 'bilder',
      nr: '3a',
      title: 'Bilder',
      purpose: 'Medizinische Abbildungen beschaffen und den passenden Lektionen zuordnen.',
      input: 'lessons.json',
      output: 'Bild-Abschnitte in lessons.json',
      tool: 'Skill content-images + scripts/acquire-images-*.ts',
      detail: [
        'Zweistufig: erst eine einmalige Beschaffung aus offen lizenzierten Quellen (Servier Medical Art unter CC BY 4.0, Medical Graphics, Wikimedia Commons) mit automatischem Aussortieren englisch beschrifteter Grafiken, dann die Zuordnung Bild zu Lektion.',
        'Die Trennung ist Absicht: Beschaffung ist teuer und selten, Zuordnung ist billig und häufig. Wer beides vermischt, lädt bei jeder Umsortierung neu herunter.',
      ],
      risk: 'Ein nachträglich eingefügtes Bild verschiebt die Abschnitts-Indizes — und bricht damit das Mitlesen der schon vertonten Lektion. Siehe Lehren.',
    },
    {
      id: 'audio',
      nr: '3b',
      title: 'Audio',
      purpose: 'Die Lektion in gesprochene Sprache überführen, wortgenau mitlesbar.',
      input: 'lessons.json',
      output: 'MP3 in R2 + audio-manifest.json',
      tool: 'npm run generate-audio — eigene Strecke, eigene Seite',
      detail: [
        'Die Vertonung ist umfangreich genug für eine eigene Beschreibung — Determinismus, Kostengate, Lautheitsnormalisierung, wortgenaues Mitlesen. Siehe die Audio-Pipeline.',
      ],
      cost: 'rund 0,15 USD je 1.000 Zeichen',
    },
    {
      id: 'supplementary',
      nr: '4',
      title: 'Begleitmaterial',
      purpose: 'Aus der fertigen Lektion das Übungsmaterial ableiten.',
      input: 'lessons.json',
      output: 'questions · flashcards · glossary · cases · self-test-questions',
      tool: 'Skill content-supplementary',
      detail: [
        'Fünf Artefakte aus einer Quelle: Prüfungsfragen, Karteikarten, Glossar, Fallbeispiele, Selbsttest. Dass sie aus der Lektion abgeleitet werden und nicht daneben entstehen, ist der Punkt — sonst driften Lehrtext und Abfrage auseinander.',
        'Die Karteikarten speisen die verteilte Wiederholung (SM-2) in der App.',
      ],
    },
    {
      id: 'qa',
      nr: '5',
      title: 'Abschluss-Prüfung',
      purpose: 'Form, Vollständigkeit und innere Widerspruchsfreiheit prüfen, bevor es in die Datenbank geht.',
      input: 'alle Artefakte des Themas',
      output: 'Prüfbericht; bei Fehlern bricht der Seed ab',
      tool: 'Skill content-qa + Validatoren in prisma/seed/',
      detail: [
        'Die Validatoren sind zod-basiert und laufen automatisch vor jedem Datenbank-Import. Geprüft werden Schema, Pflichtfelder, korrekte Antwortverweise und Eindeutigkeit der Antwortoptionen.',
        'Ein Notausgang existiert, ist aber laut: SEED_EXAM_VALIDATION_LENIENT=true überspringt einzelne fehlerhafte Fragen, statt den ganzen Import zu töten. Wer ihn benutzt, sieht es in der Ausgabe.',
      ],
    },
  ],

  gates: [
    {
      name: 'Prüfungsfragen-Schema',
      command: 'npm run validate:questions',
      promise: 'Schema, Antwort-Verweise, Eindeutigkeit der Optionen, Struktur von Kombinationsfragen',
      mode: 'gegen-null',
      current: 'blockiert den Import',
      blindFor: 'ob die markierte Antwort fachlich richtig ist',
    },
    {
      name: 'Fragen-Querprüfung',
      command: 'npm run review:questions',
      promise: 'Konsistenz über Prüfungsarchiv, Themen-Quiz und Selbsttest hinweg',
      mode: 'gegen-null',
      current: 'läuft in der CI',
    },
    {
      name: 'Fallbeispiele',
      command: 'npm run validate:cases',
      promise: 'Behandlungsgrenzen und Meldepflichten je Fall gesetzt',
      mode: 'gegen-null',
    },
    {
      name: 'Querschnitts-Konsistenz',
      command: 'npm run audit:querschnitt',
      promise: 'Der gesamte Bestand gegen sich selbst auf gegensätzliche Aussagen',
      mode: 'ratchet',
      current: '103.376 Sätze, erster Lauf 53 Gruppen, kein bestätigter Sachfehler',
      blindFor: 'Widersprüche, die nicht in der Polarität einer Aussage liegen',
    },
    {
      name: 'Distraktor-Sweep',
      command: 'npm run audit:distraktoren',
      promise: 'Kein Lehrtext wiederholt eine Aussage, die die Prüfungsbehörde als falsch markiert hat',
      mode: 'ratchet',
    },
    {
      name: 'Rechtsbelege',
      command: 'npm run audit:rechtsbelege',
      promise: 'Jede Rechtsaussage trägt eine Quelle mit Fassungsdatum',
      mode: 'ratchet',
      blindFor: 'Rechtsprechung, die den unveränderten Normtext kippt',
    },
    {
      name: 'Zahlen-Wächter',
      command: 'npm run stats:check',
      promise: 'Öffentlich behauptete Zahlen überschreiten den Seed-Bestand nicht',
      mode: 'gegen-null',
      current: 'rot nur bei Über-Behauptung',
      blindFor: 'Audiostunden und Fachbereiche — ausdrücklich ausgenommen',
    },
  ],

  artifacts: [
    { name: 'Lektionen', path: 'prisma/seed/content*/{topic}/lessons.json', purpose: 'Der Lehrtext als typisierte Abschnitte — Quelle für App, Vertonung und Mitlesen' },
    { name: 'Themenplan', path: 'docs/content/plans/content-plan-{slug}.md', purpose: 'Schnitt und Lernziele, bevor Inhalt entsteht' },
    { name: 'Begleitmaterial', path: 'prisma/seed/content*/{topic}/{questions,flashcards,glossary,cases,self-test-questions}.json', purpose: 'Abgeleitetes Übungsmaterial' },
    { name: 'Bauanleitung', path: 'docs/content-pipeline/lesson-contract.md', purpose: 'Verbindlich: was eine gute Lektion ausmacht' },
  ],

  costs: [
    { label: 'Umfang Lehrtext', value: 'rund 380.000 Wörter', hint: 'am Bildschirm gelesener Text, ohne Navigation und Bildunterschriften', source: 'docs/content-pipeline/content-stats.json, Stand 04.06.2026 (40 von heute 47 Themen)' },
    { label: 'Entspricht', value: 'rund 1.500 Normseiten', hint: 'Normseite zu 1.500 Zeichen', source: 'dieselbe Erhebung' },
    { label: 'Teuerste Stufe', value: 'Stufe 2', hint: 'Der Lehrtext selbst — Begleitmaterial und Vertonung leiten sich daraus ab', source: 'Erfahrungswert aus den Stufen-Läufen' },
  ],

  lessons: [
    {
      incident: 'Ein Flag, das fehlende Antworten als „noch zu prüfen" markierte, schaltete auch die Struktur-Prüfung ab. Der Import durfte damit seine eigenen Fehlschläge markieren — und das Tor abschalten, das sie hätte aufhalten müssen. Drei Fragen mit doppeltem Antwortblock rutschten durch.',
      rule: 'Ein Vorbehalt deckt die Antwort, nie die Struktur. Schema-Verstöße bleiben Fehler, auch mit gesetztem Flag. Wer trotzdem importieren muss, nimmt den lauten Weg über eine sichtbare Umgebungsvariable.',
    },
    {
      incident: 'Ein Umlaut-Sweep über alle Inhalte machte aus Oesophagus ein Ösophagus — und aus lateinischen Fachbegriffen, die ihre Nomenklaturform behalten müssen, ebenfalls.',
      rule: 'Die Umlaut-Regel gilt für deutsche Wörter. Fachbegriffe aus dem Lateinischen und Griechischen behalten in der Nomenklatur ihre Form. Ein „kein ae/oe"-Wächter darf sie nicht melden.',
      ticket: 'CUR-1494',
    },
    {
      incident: 'Eine Prüfungsfrage war negativ gestellt — „welche Aussage trifft nicht zu" — und die als richtig markierte Option war die fachlich falsche Aussage. Formal korrekt, inhaltlich verkehrt herum.',
      rule: 'Negativ gestellte Fragen brauchen eine eigene Prüfung. Ein Schema-Validator sieht den Unterschied nicht.',
    },
  ],

  gaps: [
    {
      topic: 'Prüfung gegen Außenquellen',
      state: 'nicht vorhanden',
      why: 'Alle Content-Wächter prüfen Form und innere Konsistenz. Keiner hält eine fachliche Aussage gegen eine Quelle außerhalb des Repos. Das ist bewusst dokumentiert und nicht verschwiegen — es ist die größte offene Flanke der Strecke.',
    },
    {
      topic: 'content-stats.json',
      state: 'veraltet (Stand 04.06.2026)',
      why: 'Behauptet 40 Themen und 263 Lektionen gegen tatsächlich 47 und 300. Enthält als einzige Quelle die Wort- und Seitenrechnung, deshalb hier mit Stand zitiert statt stillschweigend übernommen.',
    },
    {
      topic: 'exam-analysis.json',
      state: 'existiert nicht',
      why: 'Wird von der Themenplan-Stufe als optionale Quelle referenziert. Der Verweis ist tot, der Skill fängt es ab.',
    },
  ],

  sources: [
    { label: 'Die fünf Stufen im Überblick', path: 'docs/wiki/Data/pipeline-content-stufen.md' },
    { label: 'Was die Wächter nicht leisten', path: 'docs/wiki/Data/pipeline-content-fachliche-sicherung.md' },
    { label: 'Bauanleitung für Lektionen', path: 'docs/content-pipeline/lesson-contract.md' },
    { label: 'Neuerstellung ohne Quellartikel', path: 'docs/content-pipeline/creation-process.md' },
    { label: 'Migration eines Quellartikels', path: 'docs/content-pipeline/migration-process.md' },
    { label: 'Zahlen-Wächter', path: 'docs/wiki/Data/pipeline-content-zahlen-waechter.md' },
  ],

  refresh: [
    { what: 'Bestand in der Datenbank', command: 'npx tsx scripts/content-inventory.ts' },
    { what: 'Öffentliche Zahlen gegen Seed', command: 'npm run stats:check' },
    { what: 'Wort- und Seitenstatistik neu erheben', command: 'python3 scripts/content-stats.py' },
  ],
};
